import {
  BRANDING_ASSET_PATHS,
  createPortalApp,
  type PortalAppConfig,
} from '@command-center/core-platform';
import { getPortal } from '@command-center/portal-registry';
import { createPortalModule } from '@command-center/module-sdk';
import { aliceAvatarUrl } from '@command-center/oneup-portal-ui';
import { clientOneUpRecordTypes } from './record-types';

const moduleDefs = [
  ['organizations', 'Organizations', '/organizations/overview', 'Building2', 'Workspace'],
  ['websites', 'Websites', '/websites', 'Globe', 'Workspace'],
  ['work', 'Tasks & Projects', '/work/tasks', 'ClipboardList', 'Workspace'],
  ['forms', 'Form Entries', '/forms/entries', 'FileText', 'Workspace'],
  ['switchboard', 'Automations', '/switchboard', 'ToggleRight', 'AI Systems'],
  ['agents', 'AI Voice Agents', '/ai-systems/agents', 'Bot', 'AI Systems'],
  ['alice-activity', 'AI Activity Log', '/ai-systems/alice-activity', 'Sparkles', 'AI Systems'],
  ['docs', 'Docs', '/docs', 'BookOpenCheck', 'Knowledge'],
] as const;

const modules = moduleDefs.map(([id, label, route, icon, navGroup]) => createPortalModule({
  id: `client-oneup.${id}`, label, route, icon, navGroup, appTypes: ['client'],
}));

const portal = getPortal('oneup');

export const config: PortalAppConfig = {
  appId: 'client-oneup', appType: 'client', clientId: 'oneup', clientName: 'One Up',
  // Use the same #79141a One Up Hi accent as Admin. Shared semantic tones
  // continue to communicate status independently from the brand accent.
  branding: {
    name: portal.branding.name,
    logo: BRANDING_ASSET_PATHS.logo,
    iconSrc: BRANDING_ASSET_PATHS.icon,
    icon: portal.branding.iconText,
    colors: portal.branding.colors,
  },
  navigation: moduleDefs
    .filter(([id]) => id !== 'alice-activity')
    .map(([id, label, route, icon, group]) => ({ id, label, route, icon, group, moduleId: `client-oneup.${id}` })),
  modules,
  connectors: [],
  recordTypes: clientOneUpRecordTypes,
  // Alice is live and tenant-scoped. Her chat, context, voice, and file access
  // are also enforced by the Worker-issued capability contract so future tiers
  // cannot be bypassed by changing this browser configuration.
  alice: {
    enabled: true,
    availability: 'active',
    defaultState: 'open',
    assistantName: 'Alice',
    assistantSubtitle: 'Your AI copilot for this workspace.',
    avatarPath: aliceAvatarUrl,
    portalScope: 'client',
    allowedContextSources: ['page', 'website', 'project', 'task', 'form', 'form_entry', 'vault_document'],
    allowedActions: [],
    blockedActions: ['send_email_without_approval', 'delete_records', 'publish_changes', 'charge_client', 'change_permissions', 'access_unscoped_client_data'],
    enabledModes: ['context'],
    quickActionsEnabled: false,
    contextBarEnabled: true,
    suggestedActionsEnabled: false,
    voiceEnabledPlaceholder: true,
    fileUploadEnabledPlaceholder: true,
    cameraEnabledPlaceholder: false,
  },
  featureFlags: { readOnlyBeta: true, clientAlicePreview: false },
};

export const clientApp = createPortalApp(config);

export default clientApp;
